import { CSSProperties, ReactNode, useState } from "react";
import { type CSSObject } from "@emotion/react";
import type { JSX } from "@ionic/core/components";
import { IonButton, IonSpinner } from "@ionic/react";
import { BoxShadows, Breakpoints, Colors } from "swing-components";

import { useIsDesktop } from "~utils";

type buttonFill = "clear" | "outline" | "solid";

type Button = JSX.IonButton & {
  label?: ReactNode;
  fill?: buttonFill;
  isActionPending?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
};

// Stripped down button for use by sub app to support divergent development between
// teams. Plan to consolidate back into shared component after stable app release

// TODO: The `<ion-spinner />` component is rendered even though `isActionPending`
// is false

/**
 * @deprecated Use the `Button` component from `swing-components` instead.
 */
export function ButtonScore(params: Button) {
  const { label, style, disabled, isActionPending = false, fill = "solid", ...rest } = params;
  // Ionic button does not support border color change on hover
  // so instead we can use a React useState to change the border color
  const [isHover, setIsHover] = useState(false);

  const Styles = {
    outline: {
      "--color-hover": Colors.blue600,
      "--color-hover-opacity": "0.0",
      "--border-color": isHover ? Colors.blue600 : Colors.blue500,
      "--background-hover-opacity": "0.0",
    },
    solid: {
      "--background-hover": "#000",
      "--background-hover-opacity": "0.3",
      "--box-shadow": "0px 2px 4px 0px rgba(40, 60, 93, 0.16)",
    },
    disabled: {
      "--background": Colors.slate200,
      color: Colors.slate400,
      "--box-shadow": "0 2px 2px 0 #000 14.77",
      "--ripple-color": Colors.slate200,
    },
    clear: {},
  } satisfies Record<buttonFill | "disabled", CSSObject>;

  const handleMouseEnter = () => {
    setIsHover(true);
  };

  const handleMouseLeave = () => {
    setIsHover(false);
  };

  function getStyle(fill: buttonFill) {
    const buttonStyle = disabled ? Styles["disabled"] : Styles[fill];
    return { margin: 0, ...buttonStyle, ...style };
  }

  const ionFill = disabled ? "solid" : fill;

  return (
    <IonButton
      role="button"
      data-testid="button-score"
      css={[getStyle(ionFill), styles.buttonScore]}
      fill={ionFill}
      expand="block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      {...rest}
    >
      <div css={styles.buttonWrapper}>
        <IonSpinner
          name="circular"
          css={{
            margin: isActionPending ? "-5px 10px -5px -10px" : "-12px",
            visibility: isActionPending ? "visible" : "hidden",
            opacity: isActionPending ? "0.5" : "0",
            transition: "visibility 0.5s ease-in, opacity 0.5s ease-in",
            width: 24,
            height: 24,
          }}
        />
        {label}
      </div>
    </IonButton>
  );
}

type ButtonScoreWrapperProps = {
  buttonPrimary: Button;
  buttonSecondary?: Button;
  backgroundColor?: keyof typeof Colors;
  /*alert is used to display a message above the button*/
  alert?: ReactNode;
};

export function ButtonScoreWrapper(props: ButtonScoreWrapperProps) {
  const { buttonPrimary, buttonSecondary, backgroundColor, alert } = props;
  const isDesktop = useIsDesktop();

  const backgroundColorStyle = {
    backgroundColor: isDesktop ? "inherit" : backgroundColor ? Colors[backgroundColor] : "inherit",
  };

  return (
    <div
      css={[styles.actionButtonWrapper, buttonSecondary && styles.twoButtons, backgroundColorStyle]}
    >
      {alert && <div css={{ marginBottom: 16 }}>{alert}</div>}
      <div css={styles.actionButton}>
        <ButtonScore
          {...buttonPrimary}
          onClick={!buttonPrimary.disabled ? buttonPrimary.onClick : undefined}
        />
      </div>
      {buttonSecondary && (
        <div css={styles.actionButton}>
          <ButtonScore
            {...buttonSecondary}
            fill="outline"
            disabled={buttonSecondary.disabled}
            onClick={!buttonSecondary.disabled ? buttonSecondary.onClick : undefined}
          />
        </div>
      )}
    </div>
  );
}

const styles = {
  buttonWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    margin: "0 10px 0 10px",
  },
  buttonScore: {
    width: "100%",
    "--padding-bottom": 8,
    "--padding-top": 8,
    "--padding-start": 16,
    "--padding-end": 16,
    // Intentionally not using fontMedium to avoid applying a default color
    // Letting --color inherit naturally from Ionic fill setting
    fontSize: 16,
    fontWeight: 600,

    [Breakpoints.mediumDeviceAndUp]: {
      width: "auto",
    },
  },
  actionButtonWrapper: {
    position: "fixed",
    left: 0,
    right: 0,
    padding: "16px 16px",
    backgroundColor: "inherit",
    zIndex: 2,
    bottom: "calc(52px + var(--ion-safe-area-bottom, 0))",
    boxShadow: BoxShadows.default,

    [Breakpoints.mediumDeviceAndUp]: {
      position: "inherit",
      float: "right",
      padding: "24px 0",
      boxShadow: "none",
    },
  },
  actionButton: {
    [Breakpoints.mediumDeviceAndUp]: {
      /* updated per Avery, does deviate from the design system a little as is noted as such */
      minWidth: 210,
      width: "fit-content",
    },
  },
  twoButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 16,

    [Breakpoints.mediumDeviceAndUp]: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
  },
} satisfies Record<string, CSSObject>;
